type InterpolateNullsOptions = {
  keepPrefixNulls?: boolean;
  keepSuffixNulls?: boolean;
  round?: boolean;
};

type InterpolateNullsInput = (number | null)[];

// TODO: narrow the return type to number[] if both keepNulls options are false
type InterpolateNullsOutput = (number | null)[];

const defaultOptions: InterpolateNullsOptions = {
  keepPrefixNulls: false,
  keepSuffixNulls: false,
  round: false,
};

// Takes array of numbers and nulls, and replaces null values with an
// interpolated value: [1, null, 3] => [1, 2, 3]
export function interpolateNulls(
  array: InterpolateNullsInput,
  options: InterpolateNullsOptions = defaultOptions,
): InterpolateNullsOutput {
  const formatValue = (value: number) =>
    options.round ? Math.round(value) : value;

  // Copy the array
  const result = [...array];

  // Find indices of non-null values
  const nonNullIndices = result.flatMap((value, index) =>
    value !== null ? index : [],
  );

  // Handle array that is nothing but nulls and consumer has specified some or
  // all nulls be kept.
  if (
    nonNullIndices.length === 0 &&
    (options.keepPrefixNulls || options.keepSuffixNulls)
  ) {
    return result;
  }

  // Handle nulls at the beginning
  if (!options.keepPrefixNulls) {
    for (let i = 0; i < result.length; i++) {
      if (result[i] === null) {
        // Carry forward the first non-null value
        // or zero if array only contains null values
        result[i] = formatValue(result[nonNullIndices[0]] || 0);
      } else {
        break;
      }
    }
  }

  // Handle nulls at the end
  if (!options.keepSuffixNulls) {
    for (let i = result.length - 1; i >= 0; i--) {
      if (result[i] === null) {
        // Carry backward the last non-null value
        // or zero if array only contains null values
        result[i] = formatValue(
          result[nonNullIndices[nonNullIndices.length - 1]] || 0,
        );
      } else {
        break;
      }
    }
  }

  // Handle nulls in between values
  for (let i = 0; i < result.length; i++) {
    if (result[i] === null) {
      // Find the nearest non-null value before and after the null
      const prevIndex = [...nonNullIndices]
        .reverse()
        .find((index) => index < i);
      const nextIndex = nonNullIndices.find((index) => index > i);
      if (prevIndex !== undefined && nextIndex !== undefined) {
        const prevValue = result[prevIndex];
        const nextValue = result[nextIndex];
        if (prevValue !== null && nextValue !== null) {
          // Linearly interpolate between them
          const diffValue = nextValue - prevValue;
          const diffIndex = nextIndex - prevIndex;
          const stepIndex = i - prevIndex;
          result[i] = formatValue(
            prevValue + diffValue * (stepIndex / diffIndex),
          );
        }
      }
    }
  }

  return result;
}
